import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

export const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    gdpr_consent: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdpr_consent) {
      toast.error("Proszę zaakceptować zgodę na przetwarzanie danych.");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("access_key", WEB3FORMS_KEY);
      fd.append("subject", "Nowy kontakt ze strony CKK ResQ");
      fd.append("from_name", formData.name || "Strona CKK ResQ");
      fd.append("name", formData.name);
      fd.append("email", formData.email);
      fd.append("phone", formData.phone || "(nie podano)");
      fd.append("message", formData.message || "(brak treści)");
      fd.append("gdpr_consent", formData.gdpr_consent ? "TAK" : "NIE");
      fd.append("botcheck", "");

      // Try CORS first to get a real success/failure response.
      // Fall back to no-cors if CORS is blocked (some preview/proxy domains
      // are not whitelisted by Cloudflare on Web3Forms side). With no-cors
      // we cannot read the response, so we optimistically treat completion as success.
      let success = false;
      let errMsg = "";
      try {
        const response = await fetch(WEB3FORMS_ENDPOINT, {
          method: "POST",
          body: fd,
        });
        const data = await response.json();
        if (data?.success) {
          success = true;
        } else {
          errMsg = data?.message || "Web3Forms submission failed";
        }
      } catch (corsErr) {
        // CORS / network error — retry in no-cors mode (fire-and-forget)
        await fetch(WEB3FORMS_ENDPOINT, {
          method: "POST",
          mode: "no-cors",
          body: fd,
        });
        success = true; // assume success — we cannot read the opaque response
      }

      if (!success) {
        throw new Error(errMsg);
      }

      toast.success("Dziękujemy! Skontaktujemy się wkrótce.", {
        description: "Twoje zgłoszenie zostało wysłane.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        gdpr_consent: false,
      });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Wystąpił błąd. Spróbuj ponownie później.", {
        description: "Możesz też zadzwonić pod numer +48 601 486 339.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="section-spacing bg-[#F7F9F9] dark:bg-[#121418]"
      aria-label="Kontakt"
      data-testid="contact-section"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#212738] dark:text-white tracking-tight">
            KONTAKT
          </h2>
          <p className="mt-4 text-[#504D4D] dark:text-white/70">
            Masz pytania lub potrzebujesz serwisu? Skontaktuj się z nami — odpowiemy jak najszybciej.
          </p>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#212738] dark:text-white/80">
                    Imię i nazwisko *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jan Kowalski"
                    className="bg-white dark:bg-[#171A1F] border-gray-200 dark:border-white/10 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#212738] dark:text-white/80">
                    E-mail *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jan@przykladowa-klinika.pl"
                    className="bg-white dark:bg-[#171A1F] border-gray-200 dark:border-white/10 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="phone" className="text-[#212738] dark:text-white/80">
                    Telefon (opcjonalnie)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+48 123 456 789"
                    className="bg-white dark:bg-[#171A1F] border-gray-200 dark:border-white/10 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-[#212738] dark:text-white/80">
                  Treść
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Wpisz treść wiadomości..."
                  className="bg-white dark:bg-[#171A1F] border-gray-200 dark:border-white/10 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628] resize-none"
                  data-testid="contact-message-textarea"
                />
              </div>

              <label className="flex items-start space-x-3 cursor-pointer" data-testid="contact-gdpr-label">
                <Checkbox
                  id="gdpr_consent"
                  name="gdpr_consent"
                  checked={formData.gdpr_consent}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, gdpr_consent: checked }))
                  }
                  className="mt-0.5 border-gray-300 dark:border-white/30 data-[state=checked]:bg-[#EA8628] data-[state=checked]:border-[#EA8628]"
                  data-testid="contact-gdpr-checkbox"
                />
                <span className="text-sm text-[#504D4D] dark:text-white/60 leading-relaxed">
                  Wyrażam zgodę na przetwarzanie moich danych osobowych w celu obsługi zapytania. *
                </span>
              </label>

              <Button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-[#EA8628] text-white font-semibold hover:bg-[#d4791f] btn-primary px-8 py-6 text-base"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Wysyłanie...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Wyślij
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="p-6 rounded-xl bg-white dark:bg-[#171A1F] border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none">
              <h3 className="text-lg font-semibold text-[#212738] dark:text-white mb-4">
                Dane kontaktowe
              </h3>
              <div className="space-y-4">
                <a
                  href="tel:+48123110312"
                  className="flex items-center gap-3 text-[#504D4D] dark:text-white/70 hover:text-[#EA8628] transition-colors duration-200"
                  data-testid="contact-phone-link"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#EA8628]/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#EA8628]" />
                  </div>
                  <span>+48 123 110 312</span>
                </a>
                <a
                  href="mailto:biuro@ckkresq.pl"
                  className="flex items-center gap-3 text-[#504D4D] dark:text-white/70 hover:text-[#EA8628] transition-colors duration-200"
                  data-testid="contact-email-link"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#EA8628]/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#EA8628]" />
                  </div>
                  <span>biuro@ckkresq.pl</span>
                </a>
              </div>

              {/* Adresy serwisu */}
              <h4 className="text-sm font-semibold text-[#EA8628] mt-6 mb-3">
                Adresy serwisu
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 text-[#504D4D] dark:text-white/70">
                  <div className="w-8 h-8 rounded-lg bg-[#EA8628]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#EA8628]" />
                  </div>
                  <span className="text-sm">
                    ul. Gospodarska 22<br />
                    05-822 Milanówek
                  </span>
                </div>
                <div className="flex items-start gap-3 text-[#504D4D] dark:text-white/70">
                  <div className="w-8 h-8 rounded-lg bg-[#EA8628]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#EA8628]" />
                  </div>
                  <div className="text-sm">
                    <span>ul. Jana Pawła II 38/1<br />32-540 Trzebinia</span>
                    <span className="block text-xs text-[#EA8628]/80 mt-1">(adres korespondencyjny)</span>
                  </div>
                </div>
              </div>

              {/* Siedziba zarządu */}
              <h4 className="text-sm font-semibold text-[#EA8628] mt-6 mb-3">
                Siedziba zarządu
              </h4>
              <div className="flex items-start gap-3 text-[#504D4D] dark:text-white/70">
                <div className="w-8 h-8 rounded-lg bg-[#EA8628]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#EA8628]" />
                </div>
                <span className="text-sm">
                  ul. Stanisława Moniuszki 15/45<br />
                  31-523 Kraków
                </span>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-white dark:bg-[#171A1F] border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none">
              <h3 className="text-lg font-semibold text-[#212738] dark:text-white mb-2">
                Godziny pracy biura
              </h3>
              <div className="space-y-1 text-sm text-[#504D4D] dark:text-white/60">
                <p>Poniedziałek - Piątek: 8:00 - 16:00</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
